import React from "react";

export const gradeColorClass = (letter) => {
  if (["A", "B"].includes(letter)) return "text-emerald-600";
  if (letter === "C") return "text-amber-500";
  if (["D", "E"].includes(letter)) return "text-rose-600";
  return "text-slate-700";
};

export const gradeBadgeClass = (letter) => {
  if (["A", "B"].includes(letter)) return "bg-emerald-100 text-emerald-700";
  if (letter === "C") return "bg-amber-100 text-amber-700";
  if (["D", "E"].includes(letter)) return "bg-rose-100 text-rose-700";
  return "bg-slate-100 text-slate-600";
};

const LogoBox = ({ src }) =>
  src ? (
    <img src={src} alt="logo" crossOrigin="anonymous" style={{ maxWidth: "82px", maxHeight: "82px", objectFit: "contain" }} />
  ) : (
    <div style={{ width: 72, height: 72 }} className="border border-dashed border-slate-300 rounded flex items-center justify-center text-[9px] text-slate-400">LOGO</div>
  );

const SignBlock = ({ role, subrole, sig, name }) => (
  <div className="text-center" style={{ minWidth: "150px" }}>
    <p>{role}</p>
    {subrole && <p className="-mt-0.5">{subrole}</p>}
    {sig ? (
      <img src={sig} alt="ttd" crossOrigin="anonymous" style={{ height: "72px", maxWidth: "180px", objectFit: "contain", margin: "2px auto" }} />
    ) : (
      <div style={{ height: "72px" }} />
    )}
    <p className="font-semibold border-t border-slate-400 pt-1 px-4 inline-block">{name || "(.....................)"}</p>
  </div>
);

// d: { cls, academic, nonAcademic, scale, iecLogo, title, program, orgAddress, bmName, bmSignature, student, scores, comment }
export default function ReportSheet({ d, id }) {
  const { cls, academic, nonAcademic, scale, iecLogo, title, program, orgAddress, bmName, bmSignature, student, scores, comment } = d;
  const today = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const hasHeadmaster = !!cls.headmaster_name;

  return (
    <div id={id} className="report-sheet bg-white text-slate-800" style={{ width: "210mm", minHeight: "297mm", padding: "13mm", boxSizing: "border-box", fontSize: "12px" }}>
      <div className="flex items-start justify-between gap-2 border-b-2 border-gtsnavy pb-2">
        <LogoBox src={cls.school_logo} />
        <div className="text-center flex-1 px-2">
          {orgAddress && <p className="text-[10px] text-slate-600">{orgAddress}</p>}
          <h1 className="font-extrabold text-gtsnavy mt-1" style={{ fontSize: "15px" }}>{title}</h1>
          <p className="font-extrabold text-gtsnavy uppercase" style={{ fontSize: "12.5px" }}>{cls.school_full_name}</p>
          <p className="text-[11px] italic text-slate-600">{program}</p>
          <p className="text-[11px] font-semibold">Academic Year {cls.academic_year}</p>
        </div>
        <LogoBox src={iecLogo} />
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 text-[12px] my-3">
        <p><b>Name</b> : {student.name}</p>
        <p><b>Semester</b> : {cls.semester}</p>
        <p><b>Class</b> : {student.class_in_school || cls.class_in_school || "-"}</p>
        <p><b>Book/Level</b> : {student.book_level || cls.book_level || "-"}</p>
        <p><b>Student's Number</b> : {student.nis || "-"}</p>
        <p><b>Teacher</b> : {cls.teacher_full_name || cls.teacher_name}</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="font-bold text-gtsnavy mb-1 text-[12px]">Academic Score</p>
          <table className="w-full border-collapse text-[11px]">
            <thead><tr className="bg-gtsnavy/10"><th className="border border-slate-300 px-1 py-1 w-8">No</th><th className="border border-slate-300 px-2 py-1 text-left">Subject</th><th className="border border-slate-300 px-1 py-1 w-14">Score</th></tr></thead>
            <tbody>
              {academic.map((c, i) => (
                <tr key={c.key}><td className="border border-slate-300 px-1 py-1 text-center">{i + 1}</td><td className="border border-slate-300 px-2 py-1">{c.label}</td><td className="border border-slate-300 px-1 py-1 text-center">{scores.academic?.[c.key] ?? "-"}</td></tr>
              ))}
            </tbody>
          </table>
          <div className="flex gap-2 mt-2">
            <div className="flex-1 border border-slate-300 rounded p-1.5 text-center"><p className="text-[9px] text-slate-500">Average Score</p><p className="font-extrabold text-gtsnavy" style={{ fontSize: "16px" }}>{scores.average ?? "-"}</p></div>
            <div className="flex-1 border border-slate-300 rounded p-1.5 text-center"><p className="text-[9px] text-slate-500">Predicate</p><p className={`font-extrabold ${gradeColorClass(scores.letter)}`} style={{ fontSize: "16px" }}>{scores.letter ?? "-"}</p></div>
          </div>
        </div>
        <div>
          <p className="font-bold text-gtsnavy mb-1 text-[12px]">Non-Academic Score</p>
          <table className="w-full border-collapse text-[11px]">
            <thead><tr className="bg-gtsnavy/10"><th className="border border-slate-300 px-1 py-1 w-8">No</th><th className="border border-slate-300 px-2 py-1 text-left">Aspect</th><th className="border border-slate-300 px-1 py-1 w-14">Score</th></tr></thead>
            <tbody>
              {nonAcademic.map((c, i) => (
                <tr key={c.key}><td className="border border-slate-300 px-1 py-1 text-center">{i + 1}</td><td className="border border-slate-300 px-2 py-1">{c.label}</td><td className="border border-slate-300 px-1 py-1 text-center font-bold">{scores.non_academic?.[c.key] ?? "-"}</td></tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2 border border-slate-200 rounded p-2">
            <p className="text-[9px] font-semibold text-slate-500 mb-1">Note</p>
            {scale.map((b) => <p key={b.grade} className="text-[9px] leading-tight">{b.grade} ({b.label}): {b.min}–{b.max}</p>)}
          </div>
        </div>
      </div>

      <div className="mt-3">
        <p className="font-bold text-gtsnavy mb-1 text-[12px]">Teacher's Comment</p>
        <div className="border border-slate-200 rounded p-2 space-y-1 text-[11px] min-h-[64px]">
          {comment.progress && <p><b>Progress:</b> {comment.progress}</p>}
          {comment.strengths && <p><b>Strengths:</b> {comment.strengths}</p>}
          {comment.areas && <p><b>Areas for Improvement:</b> {comment.areas}</p>}
          {comment.recommendation && <p><b>Recommendation:</b> {comment.recommendation}</p>}
          {!comment.progress && !comment.strengths && !comment.areas && !comment.recommendation && <p className="text-slate-400 italic">Belum ada komentar.</p>}
        </div>
      </div>

      <p className="text-[11px] text-right mt-6 mb-1">Bekasi, {today}</p>
      <div className={`flex ${hasHeadmaster ? "justify-between" : "justify-around"} text-[11px]`}>
        {hasHeadmaster && <SignBlock role="Approved by," subrole="Headmaster" sig={cls.headmaster_signature} name={cls.headmaster_name} />}
        <SignBlock role="Branch Manager IEC Mustikajaya" sig={bmSignature} name={bmName} />
        <SignBlock role="Class Teacher" sig={cls.teacher_signature} name={cls.teacher_full_name || cls.teacher_name} />
      </div>
    </div>
  );
}
